/**
 * 定价分析口径 —— 统一语言（ubiquitous language）
 *
 * 这些概念散落在各处会导致换算方向出错（历史 bug：把「月因子」当「月数」除反，
 * 使月化人均成本被放大 ~12 倍）。因此把口径与换算集中在此单一定义，组件只调用、
 * 不再自行 ×÷。命名对齐后端 `pricing_context`。
 *
 * - 窗口值 (window value)：账本在所选日期窗口内的累计真实值（如窗口人均成本 ¥17.97）。
 * - 月因子 (monthFactor)：后端 `pricing_context.month_factor = 30 / window_days`。
 *   把「窗口累计值」折算为「平均一个月」的**乘法系数**。一年窗口 ≈ 0.08，30 天窗口 = 1.0。
 * - 月化值 (monthly value)：窗口值 × 月因子。⚠️ 是乘不是除。
 * - 账本口径 (ledger basis)：`total_cost_cny` 等未折算的窗口原值；账本总成本 / 成本构成用它。
 * - 人均 (per user)：总量 / 样本用户数。
 */
import type { CostSummary } from '@/lib/api/reports';

export type Tone = 'neutral' | 'profit' | 'warning' | 'danger' | 'info';

export interface CostMixItem {
  label: string;
  value: number;
  tone: Tone;
}

/** 窗口累计值 → 月化值：window × monthFactor（乘，不是除）。 */
export function windowToMonthly(windowValue: number, monthFactor: number): number {
  return windowValue * monthFactor;
}

/** 人均：总量 / 样本用户数（至少按 1 人，避免除零）。 */
export function perUser(total: number, users: number): number {
  return total / Math.max(1, users);
}

/** 保本月价：月化人均成本 / (1 - 退款率)。入参是 100 用户口径的月化成本。 */
export function getBreakEvenPrice(projectedCostFor100Users: number, refundRate: number): number {
  const costPerUser = projectedCostFor100Users / 100;
  const revenueAfterRefund = Math.max(0.01, 1 - refundRate);
  return costPerUser / revenueAfterRefund;
}

/** 成本构成拆分（窗口账本口径，未折算）。非零未分类成本单独列出。 */
export function getCostMixItems(summary: CostSummary): CostMixItem[] {
  const items: CostMixItem[] = [
    { label: 'LLM', value: summary.total_chat_cost_cny, tone: 'neutral' },
    { label: '图片 / TTS / STT / Realtime', value: summary.total_daily_service_cost_cny, tone: 'warning' },
  ];

  if (summary.total_soulmate_cost_cny > 0) {
    items.push({ label: 'Soulmate / 配对', value: summary.total_soulmate_cost_cny, tone: 'info' });
  }

  const knownCost = items.reduce((total, item) => total + item.value, 0);
  const otherCost = summary.total_cost_cny - knownCost;
  if (otherCost > 0.005) {
    items.push({ label: '其他 / 未分类', value: otherCost, tone: 'danger' });
  }

  return items;
}
